import * as React from "react";
import { useDataProvider, Loading } from "react-admin";
import FullCalendar from "@fullcalendar/react";
import { EventInput } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import frLocale from "@fullcalendar/core/locales/fr";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { formatDateToFrenshFormat } from "../utils/formatDateToFrenshFormat";

interface AppointmentDetails {
  id: string;
  provider: { name: string; internalCode: string };
  outlet: { name: string; internalCode: string };
  scheduledAt: string;
  status: string;
  quantityBin?: number;
  fullBinsCollectedCount?: number;
  emptyBinsDepositCount?: number;
  comment?: string;
}

const statusChoises = [
  { id: "PENDING", name: "En attente" },
  { id: "CONFIRMED", name: "Confirmé" },
  { id: "CANCELLED", name: "Annulé" },
];

const statusListLabels: Record<string, string> = statusChoises.reduce(
  (acc, { id, name }) => ({ ...acc, [id]: name }),
  {}
);

export const CalendarView: React.FC = () => {
  const dataProvider = useDataProvider();
  const navigate = useNavigate();
  const [events, setEvents] = React.useState<EventInput[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // State for dialog
  const [open, setOpen] = React.useState(false);
  const [details, setDetails] = React.useState<AppointmentDetails | null>(null);

  React.useEffect(() => {
    dataProvider
      .getList("appointments", {
        pagination: { page: 1, perPage: -1 },
        sort: { field: "scheduledAt", order: "ASC" },
        filter: {},
        meta: { populate: ["provider", "outlet"] },
      })
      .then(({ data }) => {
        const evts: EventInput[] = data.map((appt: any) => ({
          id: appt.id,
          title: `RDV – ${appt.provider.name} @ ${appt.outlet.name}`,
          start: appt.scheduledAt.slice(0, 16).replace("T", " "),
          color:
            appt.status === "CONFIRMED"
              ? "green"
              : appt.status === "CANCELLED"
              ? "red"
              : "orange",
        }));
        setEvents(evts);
      })
      .catch((err) => setError(err.message));
  }, [dataProvider]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!events) return <Loading />;

  const handleEventClick = (info: any) => {
    const id = info.event.id;
    dataProvider
      .getOne("appointments", {
        id,
        meta: { populate: ["provider", "outlet"] },
      })
      .then(({ data }) => {
        console.log(data);
        setDetails(data as AppointmentDetails);
        setOpen(true);
      })
      .catch((err) => {
        setError(err.message);
      });
  };

  return (
    <>
      <Box
        sx={{
          // Title text
          "& .fc-toolbar-title": {
            fontSize: "1.25rem",
            fontWeight: 600,
          },
          // Buttons default
          "& .fc-button": {
            backgroundColor: "secondary.main",
            color: "#fff",
            border: "none",
          },
          // Buttons on hover
          "& .fc-button:hover": {
            backgroundColor: "secondary.dark",
          },
          // “Today” button special
          "& .fc-button.fc-button-today": {
            backgroundColor: "success.main",
          },
        }}
      >
        <div style={{ padding: 16 }}>
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            locale={frLocale}
            firstDay={1}
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek",
            }}
            events={events}
            buttonText={{
              today: "Aujourd'hui",
              month: "Mois",
              week: "Semaine",
              day: "Jour",
            }}
            eventClick={handleEventClick}
            dateClick={(info) => {
              const iso = info.date.toISOString();
              navigate(
                `/appointments/create?scheduledAt=${encodeURIComponent(iso)}`
              );
            }}
            height="auto"
          />
        </div>
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Détails du RDV</DialogTitle>
        <DialogContent dividers>
          {details ? (
            <List>
              <h4>Informations</h4>
              <ListItem>
                <ListItemText
                  primary="Date & heure"
                  secondary={formatDateToFrenshFormat(
                    details.scheduledAt,
                    true
                  )}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Nom du prestataire"
                  secondary={`${details.provider.name}`}
                />
                <ListItemText
                  primary="Code intenrne du prestataire"
                  secondary={`${details.provider.internalCode}`}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Nom de l'exutoire"
                  secondary={details.outlet.name}
                />
                <ListItemText
                  primary="Code intenrne de l'exutoire"
                  secondary={details.outlet.internalCode}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Qté de caisses"
                  secondary={details.quantityBin}
                />
                <ListItemText
                  primary="Statut"
                  secondary={statusListLabels[details.status] || details.status}
                />
              </ListItem>
              {details.status === "CONFIRMED" && (
                <>
                  <hr />
                  <h4>Inventaire</h4>
                  <ListItem>
                    <ListItemText
                      primary="Nombre de bac vide récuperer"
                      secondary={details.emptyBinsDepositCount}
                    />
                    <ListItemText
                      primary="Nombre de bac collectés"
                      secondary={details.fullBinsCollectedCount}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Commentaire"
                      secondary={details.comment}
                    />
                  </ListItem>
                </>
              )}
            </List>
          ) : (
            <Typography>Chargement…</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
